import { MapsAPILoader } from '@agm/core';
import { GeoLocation } from 'ac-ui-core-local';
import {
	Component,
	ElementRef,
	EventEmitter,
	Input,
	NgZone,
	OnInit,
	Output,
	ViewChild,
	forwardRef,
} from '@angular/core';
import {
	ControlValueAccessor,
	FormControl,
	NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { Observable, empty, merge } from 'rxjs';
import { debounceTime, map } from 'rxjs/operators';

declare const google;

function getComponent(
	components: any[],
	componentType: string,
	isShort?: boolean
): string {
	const component = components.find(x =>
		x.types.some(y => y === componentType)
	);

	if (!component) return null;
	return isShort ? component.short_name : component.long_name;
}

/**
 * Provider Expression that allows azc-location-input to register as a ControlValueAccessor.
 * This allows it to support [(ngModel)].
 * @docs-private
 */
export const AZC_LOCATION_INPUT_CONTROL_VALUE_ACCESSOR: any = {
	provide: NG_VALUE_ACCESSOR,
	// tslint:disable-next-line
	useExisting: forwardRef(() => LocationInputComponent),
	multi: true,
};

@Component({
	selector: 'azc-location-input',
	templateUrl: './location-input.component.html',
	styleUrls: ['./location-input.component.scss'],
	providers: [AZC_LOCATION_INPUT_CONTROL_VALUE_ACCESSOR],
})
export class LocationInputComponent implements OnInit, ControlValueAccessor {
	@Output() private onSelect = new EventEmitter<GeoLocation>();
	@Input() public placeholder: string;
	@Input() public focus: boolean;
	@ViewChild('input') public _inputElement: ElementRef;
	public searchControl: FormControl;
	private onChange: (value) => void;
	public onTouched: () => {} = () => null;

	constructor(private _mapsAPILoader: MapsAPILoader, private _ngZone: NgZone) {}

	async ngOnInit() {
		this.searchControl = new FormControl();

		if (this.focus !== undefined) this._inputElement.nativeElement.focus();

		// Load Places Autocomplete
		await this._mapsAPILoader.load();

		const autoCompleteObserver = this.getAutoCompleteObserver();
		const inputObserver = this.searchControl.valueChanges.pipe(
			debounceTime(300),
			map(x => (x ? { fullAddress: x } : null))
		);
		merge(autoCompleteObserver, inputObserver).subscribe(x => this.onChange(x));
	}

	writeValue(obj: any): void {
		const value = (obj && obj.fullAddress) || obj;
		this.searchControl.setValue(value, { emitEvent: false });
	}

	registerOnChange(fn: (value: any) => void) {
		this.onChange = fn;
	}

	registerOnTouched(fn: any): void {
		this.onTouched = fn;
	}

	setDisabledState?(isDisabled: boolean): void {
		isDisabled ? this.searchControl.disable() : this.searchControl.enable();
	}

	private getAutoCompleteObserver(): Observable<GeoLocation> {
		if (!google) {
			console.warn('google not found for autocomplete!');
			return empty();
		}

		const autocomplete = new google.maps.places.Autocomplete(
			this._inputElement.nativeElement,
			{
				componentRestrictions: { country: ['AU', 'CA', 'NZ', 'US', 'UK'] },
			}
		);

		return new Observable<GeoLocation>(observer => {
			autocomplete.addListener('place_changed', () => {
				const place = autocomplete.getPlace();
				if (!place.address_components) return;
				let fullAddress = place.formatted_address;
				if (fullAddress && place.name !== fullAddress.split(',')[0]) {
					fullAddress = `${place.name}, ${place.formatted_address}`; // this is a workaround for AGM missing the POI address name.
				}
				const location: GeoLocation = {
					fullAddress: fullAddress,
					locality: getComponent(place.address_components, 'locality'),
					region: getComponent(
						place.address_components,
						'administrative_area_level_2'
					),
					state: getComponent(
						place.address_components,
						'administrative_area_level_1',
						true
					),
					country: getComponent(place.address_components, 'country', true),
					coords: {
						lat: place.geometry.location.lat(),
						lon: place.geometry.location.lng(),
					},
				};
				observer.next(location);
				this._ngZone.run(() => this.onSelect.emit(location));
			});
		});
	}
}
